// src/components/Sidebar.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronUp,
  FaUserShield,
  FaUsers,
  FaSchool,
  FaClipboardList,
  FaMoon,
  FaBook,
  FaCodeBranch,
  FaEnvelope,
  FaExclamationTriangle,
  FaTools,
  FaEuroSign,
  FaBriefcase,
} from "react-icons/fa";
import { FaGauge, FaChartLine, FaPeopleArrows } from "react-icons/fa6";
import { usePermissionsCtx } from "../authz/PermissionsProvider";
import { useCan } from "../authz/useCan";
import { useSchoolTasksVisibility } from "../features/featureFlags";
import { UserMenu } from "../components/nav/UserMenu";
import APP_VERSION from "../generated/appVersion";
import useBackendVersion from "../utils/useBackendVersion";
import http from "../api/apiClient";
import { fetchUnassignedChildren } from "../utils/fetchUnassignedChildren";
import { fetchWaitingWithClass } from "../utils/fetchWaitingWithClass";

const normalizeList = (data) =>
  Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
          ? data.data
          : [];

const countApplicationsPending = async () => {
  const statuses = ["PENDING", "NEW"];
  const dedupeById = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const id = item?.id || item?.applicationId;
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const filtered = await Promise.all(
    statuses.map((status) => http.get(`/applications?status=${status}`)),
  );

  const combinedFiltered = dedupeById(
    filtered
      .map((res) => normalizeList(res.data))
      .flat()
      .filter((application) =>
        statuses.includes(String(application?.status || "").toUpperCase()),
      ),
  );

  if (combinedFiltered.length) return combinedFiltered.length;

  const fallback = await http.get("/applications");
  return dedupeById(
    normalizeList(fallback.data).filter((application) =>
      statuses.includes(String(application?.status || "").toUpperCase()),
    ),
  ).length;
};

const SidebarNotificationBadge = ({
  count,
  variant = "info",
  compact = false,
  ariaLabel,
}) => {
  const numericCount = Number(count) || 0;
  if (numericCount <= 0) return null;

  const variantClasses = {
    attention:
      "bg-amber-400 text-amber-950 ring-amber-100/70 dark:bg-amber-400 dark:text-amber-950 dark:ring-amber-200/30",
    critical:
      "bg-red-500 text-white ring-red-100/70 dark:bg-red-500 dark:ring-red-200/30",
    info: "bg-slate-300 text-slate-900 ring-white/60 dark:bg-slate-500 dark:text-white dark:ring-slate-300/30",
  };
  const displayCount = numericCount > 99 ? "99+" : numericCount;

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold leading-none shadow-sm ring-1 transition duration-200 ease-out motion-reduce:transition-none ${
        compact
          ? "h-2.5 w-2.5 text-[0px]"
          : numericCount === 1
            ? "h-5 min-w-5 px-1 text-[11px]"
            : "h-5 min-w-5 px-1.5 text-[11px]"
      } ${variantClasses[variant] || variantClasses.info}`}
    >
      {compact ? <span className="sr-only">{displayCount}</span> : displayCount}
    </span>
  );
};

const Sidebar = ({
  collapsed = false,
  backendOnline,
  onToggleCollapse = () => {},
  onNavigate = () => {},
  onReportIssue,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [showBackendVersion, setShowBackendVersion] = useState(false);
  const [pendingMutationsCount, setPendingMutationsCount] = useState(0);
  const [placementAttentionCount, setPlacementAttentionCount] = useState(0);
  const [signalsAttentionCount, setSignalsAttentionCount] = useState(0);
  const [unassignedSignalsCount, setUnassignedSignalsCount] = useState(0);
  const [waitingConflictSignalsCount, setWaitingConflictSignalsCount] = useState(0);
  const [duoPendingCount, setDuoPendingCount] = useState(0);
  const [financeWaitingCount, setFinanceWaitingCount] = useState(0);

  const { version: backendVersion, loading: backendVersionLoading } =
    useBackendVersion({
      enabled: showBackendVersion,
    });

  const { ready } = usePermissionsCtx();
  const permsLoading = !ready;

  const isUnassignedRoute = location.pathname.startsWith(
    "/reports/unassigned-children",
  );
  const isWaitingConflictRoute = location.pathname.startsWith(
    "/reports/waiting-with-class",
  );
  const isCurrentSchoolYearConflictRoute = location.pathname.startsWith(
    "/reports/current-school-year-conflicts",
  );
  const isApplicationsRoute = location.pathname.startsWith(
    "/school/applications",
  );
  const isWaitingListRoute = location.pathname.startsWith(
    "/school/waiting-list",
  );
  const isMutationsRoute =
    location.pathname.startsWith("/school/mutations") ||
    location.pathname.startsWith("/administratie/mutaties");
  const isMutationProcessingRoute = location.pathname.startsWith("/admin/manage/mutation-processing");
  const isClassroomsOverviewRoute = location.pathname.startsWith(
    "/school/classrooms/overview",
  );
  const isFuturePlanningRoute = location.pathname.startsWith(
    "/plaatsing/toekomstige-planning",
  );
  const isDirectoryRoute = location.pathname.startsWith("/family/directory");
  const isFinanceActive = location.pathname.startsWith("/finance");
  const isAttendanceRoute =
    location.pathname === "/attendance" ||
    location.pathname.startsWith("/school/attendance");
  const isPlacementRootRoute =
    location.pathname.startsWith("/plaatsing") || location.pathname.startsWith("/placement");
  const isSignalsRootRoute = location.pathname.startsWith("/signaleringen");
  const isToolsRootRoute = location.pathname.startsWith("/tools");
  const isAdministrationRootRoute = location.pathname.startsWith("/administratie");
  const isSchoolSettingsRoute = location.pathname.startsWith("/admin/manage/school");
  const isAlumniRoute = location.pathname.startsWith("/reports/alumni");
  const isDuoRegistrationRoute = location.pathname.startsWith("/reports/duo-registrations");
  const isMyEnvironmentRoute =
    location.pathname === "/my" || location.pathname.startsWith("/my/");

  const isPlacementActive =
    isPlacementRootRoute ||
    isApplicationsRoute ||
    isWaitingListRoute ||
    isClassroomsOverviewRoute ||
    isFuturePlanningRoute;
  const isSignalsActive =
    isSignalsRootRoute ||
    isUnassignedRoute ||
    isWaitingConflictRoute ||
    isCurrentSchoolYearConflictRoute;
  const isToolsActive = isToolsRootRoute || isDirectoryRoute;
  const isAdministrationActive =
    isAdministrationRootRoute || isMutationsRoute || isAlumniRoute || isDuoRegistrationRoute || isSchoolSettingsRoute || isMutationProcessingRoute;

  const isSchoolActive =
    location.pathname.startsWith("/school") &&
    !isPlacementActive &&
    !isAdministrationActive &&
    !isAttendanceRoute;

  const isNoctisActive = location.pathname.startsWith("/noctis");
  const isAdminActive =
    ((location.pathname === "/admin" || location.pathname.startsWith("/admin/")) &&
      !isSchoolSettingsRoute &&
      !isMutationProcessingRoute) ||
    location.pathname.startsWith("/maintenance");
  const isMyEnvironmentActive = isMyEnvironmentRoute;
  const isFamilyActive = location.pathname.startsWith("/family") && !isToolsActive;
  const isGiteaActive = location.pathname.startsWith("/gitea");
  const isEBoekhoudenActive = location.pathname.startsWith("/integrations/eboekhouden");
  const isMailchimpActive = location.pathname.startsWith(
    "/integrations/mailchimp",
  );

  const isReportsActive =
    location.pathname.startsWith("/reports") &&
    !isUnassignedRoute &&
    !isWaitingConflictRoute &&
    !isAlumniRoute &&
    !isDuoRegistrationRoute;

  const getSectionFromPath = () => {
    if (isAttendanceRoute) return "attendance";
    if (isMyEnvironmentActive) return "myEnvironment";
    if (isPlacementActive) return "placement";
    if (isSignalsActive) return "signals";
    if (isToolsActive) return "tools";
    if (isFinanceActive) return "finance";
    if (isAdministrationActive) return "administration";
    if (isSchoolActive) return "school";
    if (isReportsActive) return "reports";
    if (isNoctisActive) return "noctis";
    if (isFamilyActive) return "family";
    if (isAdminActive) return "admin";
    if (isGiteaActive) return "gitea";
    if (isMailchimpActive) return "mailchimp";
    return null;
  };

  const [expandedSection, setExpandedSection] = useState(getSectionFromPath);

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const collapseSections = () => setExpandedSection(null);

  const NavItem = ({
    label,
    path,
    notificationCount = 0,
    notificationVariant = "info",
    isActive = location.pathname === path,
    notificationAriaLabel,
  }) => {
    if (collapsed) return null;

    return (
      <li className="min-w-0">
        <button
          type="button"
          onClick={() => {
            navigate(path);
            onNavigate?.();
          }}
          aria-current={isActive ? "page" : undefined}
          className={`relative flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-lg px-3 py-2 text-left transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 motion-reduce:transition-none ${
            isActive
              ? "bg-white/90 text-indigo-900 shadow-sm dark:bg-slate-800/90 dark:text-indigo-100"
              : "text-white/90 hover:bg-white/10 dark:text-slate-200 dark:hover:bg-white/10"
          }`}
        >
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute inset-y-1 left-0 w-0.5 rounded-r bg-violet-600 dark:bg-violet-400"
            />
          )}
          <span className="min-w-0 flex-1 truncate whitespace-nowrap">
            {label}
          </span>
          <span className="flex min-w-5 shrink-0 justify-end">
            <SidebarNotificationBadge
              count={notificationCount}
              variant={notificationVariant}
              ariaLabel={notificationAriaLabel}
            />
          </span>
        </button>
      </li>
    );
  };

  const SectionDivider = ({ label }) => {
    if (collapsed) {
      return (
        <li aria-hidden="true" className="my-3 flex justify-center">
          <div className="h-px w-8 bg-white/20" />
        </li>
      );
    }

    return (
      <li className="pt-4 pb-1">
        <div className="flex items-center gap-3 px-4">
          <div className="h-px flex-1 bg-white/15" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">
            {label}
          </span>
          <div className="h-px flex-1 bg-white/15" />
        </div>
      </li>
    );
  };

  const canSeeDashboard = useCan("dashboard", "view");

  const canSeeGiteaIssuesDirect = useCan("integration.gitea.issues", "read");
  const canSeeGiteaIssuesIntegration = useCan(
    "integration.gitea.issues",
    "read",
  );
  const canSeeGiteaIssues =
    canSeeGiteaIssuesDirect || canSeeGiteaIssuesIntegration;

  const canSeeFamilyRoot = useCan("families", "list");
  const canSeeFamiliesNavLegacy = useCan("families", "read");
  const canSeeFamiliesNavDirect = useCan("families", "read");
  const canListFamiliesNavDirect = useCan("families", "list");
  const canSeeFamiliesNav =
    canSeeFamiliesNavLegacy ||
    canSeeFamiliesNavDirect ||
    canListFamiliesNavDirect;
  const canSeeParentsNavDirect = useCan("family.parents", "read");
  const canListParentsNavDirect = useCan("family.parents", "list");
  const canSeeParentsNav =
    canSeeParentsNavDirect || canListParentsNavDirect || canSeeFamiliesNav;
  const canSeeChildrenNavDirect = useCan("family.children", "read");
  const canListChildrenNavDirect = useCan("family.children", "list");
  const canSeeChildrenNav = canSeeChildrenNavDirect || canListChildrenNavDirect;
  const canSearchFamilyBankAccounts = useCan("families.bankaccounts", "search");
  const canSeeDirectory = useCan("directory", "manage");

  const canSeeFamilySection =
    canSeeFamilyRoot || canSeeFamiliesNav || canSeeParentsNav || canSeeChildrenNav;

  const canSeeToolsSection = canSeeDirectory;
  const canSeeFinanceRoot = useCan("finance.root", "view");
  const canViewFinanceStudents = useCan("finance.reports.students", "view");
  const canViewFinanceSettings = useCan("finance.settings", "view");
  const canViewFinanceContributions = useCan("finance.contributions", "view");
  const canSeeFinanceSection = canSeeFinanceRoot || canViewFinanceStudents || canSearchFamilyBankAccounts
    || canViewFinanceSettings || canViewFinanceContributions;

  const canListTeachers = useCan("school.teachers", "list");
  const canReadTeachers = useCan("school.teachers", "read");
  const canCrudTeachers = useCan("school.teachers", "crud");
  const canManageTeachers = useCan("school.teachers", "manage");
  const canLinkSelfTeacher = useCan("school.teachers", "linkSelf");
  const canSeeTeachers =
    canListTeachers ||
    canReadTeachers ||
    canCrudTeachers ||
    canManageTeachers ||
    canLinkSelfTeacher;

  const canCrudYears = useCan("school.years", "crud");
  const canManageYears = useCan("school.years", "manage");
  const canSeeYears = canCrudYears || canManageYears;
  const canListSubjects = useCan("school.subjects", "list");
  const canReadSubjects = useCan("school.subjects", "read");
  const canCrudSubjects = useCan("school.subjects", "crud");
  const canManageSubjects = useCan("school.subjects", "manage");
  const canCreateSubjects = useCan("school.subjects", "create");
  const canUpdateSubjects = useCan("school.subjects", "update");
  const canDeleteSubjects = useCan("school.subjects", "delete");
  const canSeeSubjects = canListSubjects || canReadSubjects || canCreateSubjects || canUpdateSubjects || canDeleteSubjects || canCrudSubjects || canManageSubjects;
  const canReadSchoolCalendar = useCan("school.calendar", "read");
  const canManageSchoolCalendar = useCan("school.calendar", "manage");
  const canSeeSchoolCalendar = canReadSchoolCalendar || canManageSchoolCalendar;
  // Switched off in the backend: no menu item, whatever the permissions.
  const canSeeSchoolTasks = useSchoolTasksVisibility().admin;

  const attendanceFeatureEnabled = useCan("school.attendance", "view");
  const canListAttendance = useCan("school.attendance", "list");
  const canViewAttendance = useCan("school.attendance", "view");
  const canReadAttendance = useCan("school.attendance", "read");
  const canManageAttendance = useCan("school.attendance", "manage");
  const canMarkAttendance = useCan("school.attendance", "manage");

  const canCrudMutations = useCan("school.mutations", "crud");
  const canManageMutations = useCan("school.mutations", "manage");
  const canSeeMutations = canCrudMutations || canManageMutations;
  const canReadMutationProcessing = useCan("administration.mutation-processing", "read");
  const canSeeMutationProcessing = canReadMutationProcessing;

  const canCrudApplications = useCan("school.applications", "crud");
  const canManageApplications = useCan("school.applications", "manage");
  const canSeeApplications = canCrudApplications || canManageApplications;
  const canViewPlacementOverview = useCan("school.placement-overview", "view");

  const canListWaiting = useCan("school.waiting-list", "list");
  const canViewWaiting = useCan("school.waiting-list", "view");
  const canAssignWaiting = useCan("school.waiting-list", "assign");
  const canManageWaiting = useCan("school.waiting-list", "assign");
  const canSeeWaiting =
    canListWaiting || canViewWaiting || canAssignWaiting || canManageWaiting;

  const canViewClassrooms = useCan("school.classrooms", "view");
  const canReadClassrooms = useCan("school.classrooms", "read");
  const canListClassrooms = useCan("school.classrooms", "list");
  const canManageClassrooms = useCan("school.classrooms", "manage");
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canSeeClassrooms =
    canViewClassrooms || canReadClassrooms || canListClassrooms || canCrudClassrooms || canManageClassrooms;

  const canManageOverview = useCan("school.classrooms.overview", "manage");
  const canSeeClassroomsOverview = canManageOverview;

  const canViewFuturePlanning = useCan("school.future-planning", "view");
  const canManageFuturePlanning = useCan("school.future-planning", "manage");
  const canSeeFuturePlanning = canViewFuturePlanning || canManageFuturePlanning;

  const canSeeReportsRoot = useCan("reports.root", "view");
  const canViewFamilyReports = useCan("reports.families", "view");
  const canViewNewFamiliesReport = useCan("reports.new-families", "view");
  const canSeeStudents = useCan("reports.students", "view");
  const canViewAttendanceReports = useCan("reports.attendance", "view");
  const canViewOutingsReport = useCan("reports.outings", "view");
  const canManageDuoRegistrations = useCan("reports.duo-registrations", "manage");
  const canSeeUnassigned = useCan("signals.unassigned-children", "view");
  const canSeeWaitingWithClass = useCan("signals.waiting-list-conflicts", "view");
  const canSeeCurrentSchoolYearCheck = useCan("signals.current-school-year", "view");
  const canViewPrivacyReports = useCan("reports.privacy", "view");
  const canUseReportBuilder = useCan("reports.builder", "view");
  const canCreateReports = useCan("reports.builder", "create");
  const canReadAlumni = useCan("alumni", "read");
  const canCrudAlumni = useCan("alumni", "crud");
  const canSeeAlumni = canReadAlumni || canCrudAlumni;
  const canReadSchoolSettings = useCan("school.settings", "read");
  const canUpdateSchoolSettings = useCan("school.settings", "update");
  const canManageSchoolLinks = useCan("school.links", "manage");
  const canSeeSchoolSettings = canReadSchoolSettings || canUpdateSchoolSettings || canManageSchoolLinks;
  const canViewAnalyticsReports = useCan("admin.analytics", "read");
  const canSeeAnalyticsReports = canSeeReportsRoot && canViewAnalyticsReports;
  const canSeeReportsSection =
    canSeeReportsRoot ||
    canViewFamilyReports ||
    canViewNewFamiliesReport ||
    canViewPrivacyReports ||
    canSeeStudents ||
    (attendanceFeatureEnabled && canViewAttendanceReports) ||
    canViewOutingsReport ||
    canSeeAnalyticsReports ||
    canUseReportBuilder;

  const canManageSchoolRoot = useCan("school", "manage");
  const canReadSchoolRoot = useCan("school", "read");
  const canSeeSchoolRoot = canReadSchoolRoot || canManageSchoolRoot;

  const canSeeSchoolSection = canSeeSchoolRoot || canSeeSchoolTasks;

  const canSeeAttendanceSection =
    attendanceFeatureEnabled &&
    (canListAttendance || canReadAttendance || canViewAttendance) &&
    (canManageAttendance || canMarkAttendance);

  const canSeePlacementSection =
    canViewPlacementOverview ||
    canSeeApplications ||
    canSeeWaiting ||
    canSeeClassroomsOverview ||
    canSeeFuturePlanning;

  const getPlacementDefaultPath = () => {
    if (canViewPlacementOverview) return "/plaatsing";
    if (canSeeApplications) return "/school/applications";
    if (canSeeWaiting) return "/school/waiting-list";
    if (canSeeFuturePlanning) return "/plaatsing/toekomstige-planning";
    if (canSeeClassroomsOverview) return "/school/classrooms/overview";
    return "/plaatsing";
  };

  const canSeeAdministrationSection = canSeeMutations || canManageDuoRegistrations || canSeeAlumni || canSeeSchoolSettings;
  const administrationAttentionCount = pendingMutationsCount + duoPendingCount;

  const getAdministrationDefaultPath = () => {
    if (canSeeMutations) return "/administratie/mutaties";
    if (canManageDuoRegistrations) return "/reports/duo-registrations";
    if (canSeeAlumni) return "/reports/alumni";
    if (canSeeSchoolSettings) return "/admin/manage/school";
    return "/administratie";
  };

  useEffect(() => {
    if (!canSeeMutations) {
      setPendingMutationsCount(0);
      return;
    }

    let active = true;

    const loadPendingMutations = async () => {
      try {
        const res = await http.get("/mutation-requests");

        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.content)
            ? res.data.content
            : [];

        const count = list.filter(
          (request) =>
            String(request?.status || "").toUpperCase() === "PENDING",
        ).length;

        if (active) setPendingMutationsCount(count);
      } catch {
        if (active) setPendingMutationsCount(0);
      }
    };

    loadPendingMutations();

    return () => {
      active = false;
    };
  }, [canSeeMutations]);

  const canSeeSignalsSection =
    canSeeUnassigned || canSeeWaitingWithClass || canSeeCurrentSchoolYearCheck;

  useEffect(() => {
    if (!canSeeApplications) {
      setPlacementAttentionCount(0);
      return;
    }

    let active = true;

    const loadPlacementAttention = async () => {
      try {
        const count = await countApplicationsPending();
        if (active) setPlacementAttentionCount(count);
      } catch {
        if (active) setPlacementAttentionCount(0);
      }
    };

    loadPlacementAttention();

    return () => {
      active = false;
    };
  }, [canSeeApplications]);

  useEffect(() => {
    if (!canSeeSignalsSection) {
      setSignalsAttentionCount(0);
      setUnassignedSignalsCount(0);
      setWaitingConflictSignalsCount(0);
      return;
    }

    let active = true;

    const loadSignalsAttention = async () => {
      try {
        const [unassignedResult, waitingConflictResult] = await Promise.all([
          canSeeUnassigned ? fetchUnassignedChildren() : Promise.resolve([]),
          canSeeWaitingWithClass ? fetchWaitingWithClass() : Promise.resolve([]),
        ]);
        const activeChildren = (result) =>
          normalizeList(result).filter(
            (child) => !child?.withdrawnAt && !child?.deceasedAt,
          );
        const unassignedChildren = activeChildren(unassignedResult);
        const waitingConflictChildren = activeChildren(waitingConflictResult);
        const uniqueChildren = new Map();

        [...unassignedChildren, ...waitingConflictChildren].forEach((child, index) => {
          const key = child?.uuid || child?.id || `signal-${index}`;
          uniqueChildren.set(String(key), child);
        });

        if (active) {
          setSignalsAttentionCount(uniqueChildren.size);
          setUnassignedSignalsCount(unassignedChildren.length);
          setWaitingConflictSignalsCount(waitingConflictChildren.length);
        }
      } catch {
        if (active) {
          setSignalsAttentionCount(0);
          setUnassignedSignalsCount(0);
          setWaitingConflictSignalsCount(0);
        }
      }
    };

    loadSignalsAttention();

    return () => {
      active = false;
    };
  }, [canSeeSignalsSection, canSeeUnassigned, canSeeWaitingWithClass]);

  const canManageAdminUsers = useCan("admin.users", "manage");
  const canManageNotifications = useCan("admin.notifications", "manage");
  const canSendNotifications = useCan("admin.notifications", "send");
  const canViewLogs = useCan("admin.audit", "read");
  const canSeeAdminRoot = useCan("admin", "view");

  const canSeeNotificationsAdmin =
    canManageNotifications || canSendNotifications;

  const canManageBackupsPermission = useCan("admin.backups", "manage");
  const canManageBackups = canManageBackupsPermission;

  const canSeeNoctis = useCan("integration.noctis.object-types", "view");

  const canManageOutingInfo = useCan("outing.info", "manage");
  const canReadOutingInfo = useCan("outing.info", "read");
  const canViewMyEnvironment = useCan("my.environment", "view");
  const canViewMyClassrooms = useCan("my.classrooms", "view");
  const canViewMyDetails = useCan("my.teacher-details", "view");
  const canViewMyParentMeetings = useCan("my.parent-meetings", "view");
  const canViewMyAgendaActivities = useCan("my.agenda-activities", "view");
  const canReadClassroomCalendar = useCan("classroom.calendar", "read");
  const canManageClassroomCalendar = useCan("classroom.calendar", "manage");
  const canViewMyAgendaActivitiesPage =
    canViewMyAgendaActivities ||
    canReadClassroomCalendar ||
    canManageClassroomCalendar ||
    canReadOutingInfo ||
    canManageOutingInfo ||
    canViewOutingsReport;

  const canSeeMyEnvironment =
    canViewMyEnvironment ||
    canViewMyClassrooms ||
    canViewMyDetails ||
    canViewMyParentMeetings ||
    canViewMyAgendaActivitiesPage;

  const canNewsletterStatus = useCan("newsletter.recipients", "status");
  const canNewsletterExport = useCan("newsletter.recipients", "export");
  const canNewsletterSync = useCan("newsletter.recipients", "sync");
  const canManageEmailValidation = useCan("email.validation", "manage");

  const canSeeMailchimp =
    canNewsletterStatus || (canNewsletterExport && canNewsletterSync);

  const canSeeMaintenance =
    canManageOutingInfo || canSeeMailchimp || canManageEmailValidation;

  const canSeeAdminSection =
    canSeeAdminRoot ||
    canManageAdminUsers ||
    canSeeNotificationsAdmin ||
    canManageBackups ||
    canSeeMaintenance ||
    canViewLogs;

  const canSeeIntegrationsSection =
    canSeeMailchimp || canSeeNoctis || canSeeGiteaIssues || canViewFinanceSettings;

  const getAdminDefaultPath = () => {
    if (canSeeAdminRoot) return "/admin";
    if (canSeeNotificationsAdmin) return "/admin/notifications";
    if (canManageBackups) return "/admin/backups";
    if (canViewLogs) return "/admin/logs";
    return "/admin";
  };

  const getReportsDefaultPath = () => {
    if (canSeeReportsRoot) return "/reports";
    if (canViewFamilyReports) return "/reports/families";
    if (canViewNewFamiliesReport) return "/reports/new-families";
    if (canViewPrivacyReports) return "/reports/privacy";
    if (canSeeStudents) return "/reports/students";
    if (attendanceFeatureEnabled && canViewAttendanceReports)
      return "/reports/attendance";
    if (canViewOutingsReport) return "/reports/outings";
    if (canUseReportBuilder) return "/reports/builder";
    return "/reports";
  };

  useEffect(() => {
    if (!canManageDuoRegistrations) {
      setDuoPendingCount(0);
      return;
    }
    let active = true;
    const load = () => http.get("/reports/duo-registrations?completed=false")
      .then((response) => {
        if (active) setDuoPendingCount(normalizeList(response.data).length);
      })
      .catch(() => {
        if (active) setDuoPendingCount(0);
      });
    load();
    window.addEventListener("lumen:duo-registration-changed", load);
    return () => {
      active = false;
      window.removeEventListener("lumen:duo-registration-changed", load);
    };
  }, [canManageDuoRegistrations]);

  // Payments from e-Boekhouden still to link; the contributions page announces changes.
  useEffect(() => {
    if (!canViewFinanceContributions) {
      setFinanceWaitingCount(0);
      return;
    }
    let active = true;
    const load = () => http.get("/finance/payments/waiting/count")
      .then((response) => {
        if (active) setFinanceWaitingCount(Number(response.data?.count) || 0);
      })
      .catch(() => {
        if (active) setFinanceWaitingCount(0);
      });
    load();
    window.addEventListener("lumen:finance-waiting-changed", load);
    return () => {
      active = false;
      window.removeEventListener("lumen:finance-waiting-changed", load);
    };
  }, [canViewFinanceContributions]);

  useEffect(() => {
    const activeSection = getSectionFromPath();

    setExpandedSection((prev) => {
      if (!activeSection) return prev;
      if (prev === activeSection) return prev;
      return activeSection;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div
      className={`relative h-full p-6 lg:p-6 sticky top-0 min-h-screen transition-all print:hidden text-white dark:text-slate-100 ${
        collapsed ? "px-3" : "px-6"
      } bg-gradient-to-b from-indigo-800/95 via-indigo-900/95 to-slate-950/95 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 border-r border-white/15 dark:border-slate-800/70 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.85)] backdrop-blur-xl`}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-6 -right-6 w-12 rounded-full bg-gradient-to-r from-indigo-900/35 via-indigo-900/15 to-transparent blur-2xl"
      />

      <button
        type="button"
        onClick={onToggleCollapse}
        className={`hidden lg:flex absolute top-1/2 -right-3 z-10 h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-all duration-300 hover:bg-indigo-500 focus:outline-none ${
          collapsed ? "translate-x-1 rotate-180" : "translate-x-1"
        }`}
        aria-label={
          collapsed
            ? t("navigation.expand_sidebar", {
                defaultValue: "Expand sidebar",
              })
            : t("navigation.collapse_sidebar", {
                defaultValue: "Collapse sidebar",
              })
        }
      >
        <FaChevronLeft className="transition-transform duration-300" />
      </button>

      <h2
        className={`text-2xl font-bold mb-6 cursor-pointer transition-all duration-300 ${
          collapsed ? "text-center text-xl" : ""
        }`}
        onClick={() => navigate("/")}
      >
        {collapsed ? "L" : "LUMEN"}
      </h2>

      <div
        className={`pr-2 -mr-2 overflow-y-auto ${
          collapsed ? "" : "pb-32"
        } max-h-[calc(100vh-140px)]`}
      >
        {permsLoading ? (
          <div className="px-4 py-2 text-sm text-white/80 dark:text-slate-300">
            Loading permissions…
          </div>
        ) : (
          <nav>
            <ul className={`space-y-2 ${collapsed ? "items-center" : ""}`}>
              {canSeeDashboard && (
                <li>
                  <button
                    onClick={() => {
                      navigate("/dashboard");
                      onNavigate?.();
                    }}
                    className={`w-full flex items-center py-2 rounded transition ${
                      collapsed ? "justify-center" : "px-4 space-x-3"
                    } ${
                      location.pathname === "/dashboard"
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <FaGauge />
                    <span className={`${collapsed ? "sr-only" : "inline"}`}>
                      Dashboard
                    </span>
                  </button>
                </li>
              )}

              {canSeeAttendanceSection && (
                <li>
                  <button
                    onClick={() => {
                      navigate("/attendance");
                      onNavigate?.();
                    }}
                    className={`w-full flex items-center py-2 rounded transition ${
                      collapsed ? "justify-center" : "px-4 space-x-3"
                    } ${
                      isAttendanceRoute
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <FaClipboardList />
                    <span className={`${collapsed ? "sr-only" : "inline"}`}>
                      {t("attendance.today.nav_label", {
                        defaultValue: "Aanwezigheid",
                      })}
                    </span>
                  </button>
                </li>
              )}


              {canSeeMyEnvironment && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isMyEnvironmentActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("myEnvironment");
                        navigate("/my");
                        onNavigate?.();
                      }}
                      className={`relative flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaBriefcase />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.my_environment", {
                            defaultValue: "My environment",
                          })}
                        </span>
                      </span>
                    </button>

                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection("myEnvironment")}
                        className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                        aria-label={
                          expandedSection === "myEnvironment"
                            ? t("navigation.collapse_my_environment", {
                                defaultValue: "Collapse my environment",
                              })
                            : t("navigation.expand_my_environment", {
                                defaultValue: "Expand my environment",
                              })
                        }
                      >
                        {expandedSection === "myEnvironment" ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "myEnvironment" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canViewMyClassrooms && (
                        <NavItem
                          label={t("myEnvironment.classrooms.title", {
                            defaultValue: "My classrooms",
                          })}
                          path="/my/classrooms"
                        />
                      )}
                      {canViewMyParentMeetings && (
                        <NavItem
                          label={t("navigation.my_parent_meetings", {
                            defaultValue: "Parent meetings",
                          })}
                          path="/my/parent-meetings"
                        />
                      )}
                      {canViewMyAgendaActivitiesPage && (
                        <NavItem
                          label={t("myEnvironment.outings.agendaTitle", {
                            defaultValue: "Agenda & activities",
                          })}
                          path="/my/outings"
                        />
                      )}
                      {canViewMyDetails && (
                        <NavItem
                          label={t("myEnvironment.teacherDetails.title", {
                            defaultValue: "My details",
                          })}
                          path="/my/details"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeFamilySection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isFamilyActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("family");
                        navigate("/family");
                        onNavigate?.();
                      }}
                      className={`relative flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaUsers />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("family.name")}
                        </span>
                      </span>
                    </button>

                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection("family")}
                        className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                        aria-label={
                          expandedSection === "family"
                            ? t("navigation.collapse_family", {
                                defaultValue: "Collapse family",
                              })
                            : t("navigation.expand_family", {
                                defaultValue: "Expand family",
                              })
                        }
                      >
                        {expandedSection === "family" ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "family" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canSeeFamiliesNav && (
                        <NavItem
                          label={t("family.name")}
                          path="/family/families"
                        />
                      )}
                      {canSeeParentsNav && (
                        <NavItem
                          label={t("family.parents.name")}
                          path="/family/parents"
                        />
                      )}
                      {canSeeChildrenNav && (
                        <NavItem
                          label={t("children.name")}
                          path="/family/children"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeePlacementSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isPlacementActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("placement");
                        navigate(getPlacementDefaultPath());
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <span className="inline-flex">
                          <FaPeopleArrows />
                        </span>
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.placement", {
                            defaultValue: "Plaatsing",
                          })}
                        </span>
                      </span>
                      {collapsed && (
                        <span className="ml-1 self-start">
                          <SidebarNotificationBadge
                            count={placementAttentionCount}
                            variant="attention"
                            compact
                            ariaLabel={t(
                              "navigation.placement_attention_count",
                              {
                                defaultValue:
                                  "{{count}} placement items need attention",
                                count: placementAttentionCount,
                              },
                            )}
                          />
                        </span>
                      )}
                    </button>

                    {!collapsed && (
                      <>
                        <span
                          className={`ml-2 flex min-w-5 justify-end transition-all duration-200 motion-reduce:transition-none ${
                            expandedSection === "placement"
                              ? "pointer-events-none scale-95 opacity-0"
                              : "scale-100 opacity-100"
                          }`}
                        >
                          <SidebarNotificationBadge
                            count={
                              expandedSection !== "placement"
                                ? placementAttentionCount
                                : 0
                            }
                            variant="attention"
                            ariaLabel={t(
                              "navigation.placement_attention_count",
                              {
                                defaultValue:
                                  "{{count}} placement items need attention",
                                count: placementAttentionCount,
                              },
                            )}
                          />
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleSection("placement")}
                          className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                          aria-label={
                            expandedSection === "placement"
                              ? t("navigation.collapse_placement", {
                                  defaultValue: "Collapse placement",
                                })
                              : t("navigation.expand_placement", {
                                  defaultValue: "Expand placement",
                                })
                          }
                        >
                          {expandedSection === "placement" ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {!collapsed && expandedSection === "placement" && (
                    <ul className="ml-3 mt-1 min-w-0 space-y-1 pr-1">
                      {canSeeApplications && (
                        <NavItem
                          label={t("applications.name")}
                          path="/school/applications"
                          notificationCount={placementAttentionCount}
                          notificationVariant="attention"
                          notificationAriaLabel={t(
                            "navigation.placement_attention_count",
                            {
                              defaultValue:
                                "{{count}} placement items need attention",
                              count: placementAttentionCount,
                            },
                          )}
                        />
                      )}
                      {canSeeWaiting && (
                        <NavItem
                          label={t("waiting-list.name")}
                          path="/school/waiting-list"
                        />
                      )}
                      {canSeeFuturePlanning && (
                        <NavItem
                          label={t("navigation.futurePlanning", {
                            defaultValue: "Future planning",
                          })}
                          path="/plaatsing/toekomstige-planning"
                        />
                      )}
                      {canSeeClassroomsOverview && (
                        <NavItem
                          label={t("classrooms.overview_management.title")}
                          path="/school/classrooms/overview"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeSchoolSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isSchoolActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("school");
                        if (canSeeSchoolRoot) {
                          navigate("/school");
                        }
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaSchool />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("school.name")}
                        </span>
                      </span>
                    </button>

                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection("school")}
                        className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                        aria-label={
                          expandedSection === "school"
                            ? t("navigation.collapse_school", {
                                defaultValue: "Collapse school",
                              })
                            : t("navigation.expand_school", {
                                defaultValue: "Expand school",
                              })
                        }
                      >
                        {expandedSection === "school" ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "school" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canSeeTeachers && (
                        <NavItem
                          label={t("teachers.name")}
                          path="/school/teachers"
                        />
                      )}
                      {canSeeClassrooms && (
                        <NavItem
                          label={t("classrooms.name")}
                          path="/school/classrooms"
                        />
                      )}
                      {canSeeYears && (
                        <NavItem label={t("years.name")} path="/school/years" />
                      )}
                      {canSeeSubjects && (
                        <NavItem label={t("subjects.title", { defaultValue: "Subjects" })} path="/school/subjects" />
                      )}
                      {canSeeSchoolCalendar && (
                        <NavItem
                          label={t("schoolCalendar.title", {
                            defaultValue: "Schoolplanning",
                          })}
                          path="/school/school-calendar"
                        />
                      )}
                      {canSeeSchoolTasks && <NavItem label={t("schoolTasks.title", { defaultValue: "School tasks" })} path="/school/school-tasks" />}
                    </ul>
                  )}
                </li>
              )}

              {canSeeSignalsSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isSignalsActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("signals");
                        navigate("/signaleringen");
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <span className="inline-flex">
                          <FaExclamationTriangle />
                        </span>
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.signals", {
                            defaultValue: "Signaleringen",
                          })}
                        </span>
                      </span>
                      {collapsed && (
                        <span className="ml-1 self-start">
                          <SidebarNotificationBadge
                            count={signalsAttentionCount}
                            variant="attention"
                            compact
                            ariaLabel={t("navigation.signals_attention_count", {
                              defaultValue: "{{count}} signals need attention",
                              count: signalsAttentionCount,
                            })}
                          />
                        </span>
                      )}
                    </button>

                    {!collapsed && (
                      <>
                        <span
                          className={`ml-2 flex min-w-5 justify-end transition-all duration-200 motion-reduce:transition-none ${
                            expandedSection === "signals"
                              ? "pointer-events-none scale-95 opacity-0"
                              : "scale-100 opacity-100"
                          }`}
                        >
                          <SidebarNotificationBadge
                            count={expandedSection !== "signals" ? signalsAttentionCount : 0}
                            variant="attention"
                            ariaLabel={t("navigation.signals_attention_count", {
                              defaultValue: "{{count}} signals need attention",
                              count: signalsAttentionCount,
                            })}
                          />
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleSection("signals")}
                          className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                          aria-label={
                            expandedSection === "signals"
                              ? t("navigation.collapse_signals", {
                                  defaultValue: "Collapse signals",
                                })
                              : t("navigation.expand_signals", {
                                  defaultValue: "Expand signals",
                                })
                          }
                        >
                          {expandedSection === "signals" ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {!collapsed && expandedSection === "signals" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canSeeUnassigned && (
                        <NavItem
                          label={t("reports.unassigned.name")}
                          path="/reports/unassigned-children"
                          notificationCount={unassignedSignalsCount}
                          notificationVariant="attention"
                          notificationAriaLabel={t("navigation.signals_attention_count", {
                            defaultValue: "{{count}} signals need attention",
                            count: unassignedSignalsCount,
                          })}
                        />
                      )}
                      {canSeeWaitingWithClass && (
                        <NavItem
                          label={t("reports.waiting_with_class.name")}
                          path="/reports/waiting-with-class"
                          notificationCount={waitingConflictSignalsCount}
                          notificationVariant="attention"
                          notificationAriaLabel={t("navigation.signals_attention_count", {
                            defaultValue: "{{count}} signals need attention",
                            count: waitingConflictSignalsCount,
                          })}
                        />
                      )}
                      {canSeeCurrentSchoolYearCheck && (
                        <NavItem
                          label={t("signals.currentSchoolYear.title", {
                            defaultValue: "Current school year check",
                          })}
                          path="/reports/current-school-year-conflicts"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeReportsSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isReportsActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("reports");
                        navigate(getReportsDefaultPath());
                        onNavigate?.();
                      }}
                      className={`relative flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaChartLine />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("reports.title")}
                        </span>
                      </span>
                    </button>

                    {!collapsed && (
                        <button
                          type="button"
                          onClick={() => toggleSection("reports")}
                          className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                          aria-label={
                            expandedSection === "reports"
                              ? t("navigation.collapse_reports", {
                                  defaultValue: "Collapse reports",
                                })
                              : t("navigation.expand_reports", {
                                  defaultValue: "Expand reports",
                                })
                          }
                        >
                          {expandedSection === "reports" ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "reports" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canViewPrivacyReports && (
                        <NavItem
                          label={t("reports.privacy.title")}
                          path="/reports/privacy"
                        />
                      )}
                      {canViewFamilyReports && (
                        <NavItem
                          label={t("reports.families.title", {
                            defaultValue: "Family lists",
                          })}
                          path="/reports/families"
                        />
                      )}
                      {canViewNewFamiliesReport && (
                        <NavItem
                          label={t("reports.new_families.title", {
                            defaultValue: "New families",
                          })}
                          path="/reports/new-families"
                        />
                      )}
                      {canSeeAnalyticsReports && (
                        <NavItem
                          label={t("reports.analytics.title", {
                            defaultValue: "Analytics",
                          })}
                          path="/reports/analytics"
                        />
                      )}
                      {canSeeStudents && (
                        <NavItem
                          label={t("reports.students.name")}
                          path="/reports/students"
                        />
                      )}
                      {attendanceFeatureEnabled && canViewAttendanceReports && (
                        <NavItem
                          label={t("reports.attendance.nav", {
                            defaultValue: "Attendance",
                          })}
                          path="/reports/attendance"
                        />
                      )}
                      {canViewOutingsReport && (
                        <NavItem
                          label={t("reports.outings.card_title", {
                            defaultValue: "Uitjes",
                          })}
                          path="/reports/outings"
                        />
                      )}
                      {canUseReportBuilder && (
                        <NavItem
                          label={
                            canCreateReports
                              ? t("reports.builder.title", {
                                  defaultValue: "Report builder",
                                })
                              : t("reports.builder.library_nav", {
                                  defaultValue: "Report library",
                                })
                          }
                          path="/reports/builder"
                          isActive={location.pathname.startsWith("/reports/builder")}
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeFinanceSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isFinanceActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("finance");
                        navigate("/finance");
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaEuroSign />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.finance", { defaultValue: "Finance" })}
                        </span>
                      </span>
                      {collapsed && (
                        <span className="ml-1 self-start">
                          <SidebarNotificationBadge
                            count={financeWaitingCount}
                            variant="attention"
                            compact
                            ariaLabel={t("navigation.finance_waiting_count", {
                              defaultValue: "{{count}} payment(s) to link",
                              count: financeWaitingCount,
                            })}
                          />
                        </span>
                      )}
                    </button>

                    {!collapsed && (
                      <span
                        className={`ml-2 flex min-w-5 justify-end transition-all duration-200 motion-reduce:transition-none ${
                          expandedSection === "finance"
                            ? "pointer-events-none scale-95 opacity-0"
                            : "scale-100 opacity-100"
                        }`}
                      >
                        <SidebarNotificationBadge
                          count={expandedSection !== "finance" ? financeWaitingCount : 0}
                          variant="attention"
                          ariaLabel={t("navigation.finance_waiting_count", {
                              defaultValue: "{{count}} payment(s) to link",
                              count: financeWaitingCount,
                            })}
                        />
                      </span>
                    )}
                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection("finance")}
                        className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                        aria-label={
                          expandedSection === "finance"
                            ? t("navigation.collapse_finance", {
                                defaultValue: "Collapse finance",
                              })
                            : t("navigation.expand_finance", {
                                defaultValue: "Expand finance",
                              })
                        }
                      >
                        {expandedSection === "finance" ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "finance" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {(canViewFinanceContributions || canViewFinanceStudents) && (
                        <NavItem
                          label={t("finance.contributions.title", {
                            defaultValue: "Parent contributions",
                          })}
                          path="/finance/payments"
                          notificationCount={canViewFinanceContributions ? financeWaitingCount : 0}
                          notificationVariant="attention"
                          notificationAriaLabel={t("navigation.finance_waiting_count", {
                              defaultValue: "{{count}} payment(s) to link",
                              count: financeWaitingCount,
                            })}
                        />
                      )}
                      {canSearchFamilyBankAccounts && (
                        <NavItem
                          label={t("family.bank_account_lookup.title", {
                            defaultValue: "Bank account lookup",
                          })}
                          path="/finance/bank-account-lookup"
                        />
                      )}
                      {(canViewFinanceContributions || canViewFinanceSettings) && (
                        <NavItem
                          label={t("finance.import_log.title", {
                            defaultValue: "Connection log",
                          })}
                          path="/finance/imports"
                        />
                      )}
                      {canViewFinanceSettings && (
                        <NavItem
                          label={t("finance.settings.nav", {
                            defaultValue: "Settings",
                          })}
                          path="/finance/settings"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeAdministrationSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isAdministrationActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("administration");
                        navigate(getAdministrationDefaultPath());
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <span className="inline-flex">
                          <FaClipboardList />
                        </span>
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.administration", {
                            defaultValue: "Administration",
                          })}
                        </span>
                      </span>
                      {collapsed && (
                        <span className="ml-1 self-start">
                          <SidebarNotificationBadge
                            count={administrationAttentionCount}
                            variant="attention"
                            compact
                            ariaLabel={t("navigation.administration_attention_count", {
                              defaultValue:
                                "{{count}} administration items need attention",
                              count: administrationAttentionCount,
                            })}
                          />
                        </span>
                      )}
                    </button>

                    {!collapsed && (
                      <>
                        <span
                          className={`ml-2 flex min-w-5 justify-end transition-all duration-200 motion-reduce:transition-none ${
                            expandedSection === "administration"
                              ? "pointer-events-none scale-95 opacity-0"
                              : "scale-100 opacity-100"
                          }`}
                        >
                          <SidebarNotificationBadge
                            count={
                              expandedSection !== "administration"
                                ? administrationAttentionCount
                                : 0
                            }
                            variant="attention"
                            ariaLabel={t("navigation.administration_attention_count", {
                              defaultValue:
                                "{{count}} administration items need attention",
                              count: administrationAttentionCount,
                            })}
                          />
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleSection("administration")}
                          className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                          aria-label={
                            expandedSection === "administration"
                              ? t("navigation.collapse_administration", {
                                  defaultValue: "Collapse administration",
                                })
                              : t("navigation.expand_administration", {
                                  defaultValue: "Expand administration",
                                })
                          }
                        >
                          {expandedSection === "administration" ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {!collapsed && expandedSection === "administration" && (
                    <ul className="ml-3 mt-1 min-w-0 space-y-1 pr-1">
                      {canSeeMutations && (
                        <NavItem
                          label={t("mutations.name")}
                          path="/administratie/mutaties"
                          notificationCount={pendingMutationsCount}
                          notificationVariant="attention"
                          notificationAriaLabel={t(
                            "mutations.pending_count",
                            {
                              defaultValue:
                                "{{count}} mutation requests need attention",
                              count: pendingMutationsCount,
                            },
                          )}
                        />
                      )}
                      {canManageDuoRegistrations && (
                        <NavItem
                          label={t("duo.title", { defaultValue: "DUO registrations" })}
                          path="/reports/duo-registrations"
                          notificationCount={duoPendingCount}
                          notificationVariant="attention"
                          notificationAriaLabel={t("duo.pending_count", {
                            defaultValue: "{{count}} pending DUO registrations",
                            count: duoPendingCount,
                          })}
                        />
                      )}
                      {canSeeAlumni && (
                        <NavItem
                          label={t("reports.alumni.card_title", {
                            defaultValue: "Alumni",
                          })}
                          path="/reports/alumni"
                        />
                      )}
                      {(canSeeMutationProcessing || canSeeSchoolSettings) && (
                        <>
                          <li className="px-4 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">
                            {t("navigation.management", {
                              defaultValue: "Management",
                            })}
                          </li>
                          {canSeeMutationProcessing && (
                            <NavItem
                              label={t("admin.mutation_processing.title", {
                                defaultValue: "Mutation processing",
                              })}
                              path="/admin/manage/mutation-processing"
                            />
                          )}
                          {canSeeSchoolSettings && (
                          <NavItem
                            label={t("admin.school_settings.title", {
                              defaultValue: "School",
                            })}
                            path="/admin/manage/school"
                          />
                          )}
                        </>
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeToolsSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isToolsActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("tools");
                        navigate("/tools");
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaTools />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.tools", { defaultValue: "Tools" })}
                        </span>
                      </span>
                    </button>

                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection("tools")}
                        className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                        aria-label={
                          expandedSection === "tools"
                            ? t("navigation.collapse_tools", {
                                defaultValue: "Collapse tools",
                              })
                            : t("navigation.expand_tools", {
                                defaultValue: "Expand tools",
                              })
                        }
                      >
                        {expandedSection === "tools" ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "tools" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canSeeDirectory && (
                        <NavItem
                          label={t("directory.title", {
                            defaultValue: "Directory",
                          })}
                          path="/family/directory"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeAdminSection && (
                <li>
                  <div
                    className={`w-full flex items-center rounded transition py-2 ${
                      collapsed ? "justify-center" : "justify-between px-4"
                    } ${
                      isAdminActive
                        ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                        : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSection("admin");
                        navigate(getAdminDefaultPath());
                        onNavigate?.();
                      }}
                      className={`flex items-center ${
                        collapsed ? "" : "flex-1"
                      } focus:outline-none`}
                    >
                      <span
                        className={`flex items-center ${
                          collapsed ? "" : "space-x-3"
                        }`}
                      >
                        <FaUserShield />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          {t("navigation.management", {
                            defaultValue: "Beheer",
                          })}
                        </span>
                      </span>
                    </button>

                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() => toggleSection("admin")}
                        className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                        aria-label={
                          expandedSection === "admin"
                            ? t("navigation.collapse_admin", {
                                defaultValue: "Collapse admin",
                              })
                            : t("navigation.expand_admin", {
                                defaultValue: "Expand admin",
                              })
                        }
                      >
                        {expandedSection === "admin" ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                    )}
                  </div>

                  {!collapsed && expandedSection === "admin" && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {canManageAdminUsers && (
                        <NavItem label="Users" path="/admin/users" />
                      )}
                      {canManageAdminUsers && (
                        <NavItem label="Roles" path="/admin/roles" />
                      )}
                      {canSeeNotificationsAdmin && (
                        <NavItem
                          label={t("admin.notifications.title", {
                            defaultValue: "Notifications",
                          })}
                          path="/admin/notifications"
                        />
                      )}
                      {canManageBackups && (
                        <NavItem
                          label={t("admin.backups.title", {
                            defaultValue: "Backups",
                          })}
                          path="/admin/backups"
                        />
                      )}
                      {canViewLogs && (
                        <NavItem
                          label={t("admin.logs.title", {
                            defaultValue: "Logging",
                          })}
                          path="/admin/logs"
                        />
                      )}
                      {canSeeMaintenance && (
                        <NavItem
                          label={t("admin.maintenance.title", {
                            defaultValue: "Maintenance",
                          })}
                          path="/maintenance"
                        />
                      )}
                    </ul>
                  )}
                </li>
              )}

              {canSeeIntegrationsSection && (
                <>
                  <SectionDivider
                    label={t("navigation.integrations", {
                      defaultValue: "Integraties",
                    })}
                  />

                  {canSeeGiteaIssues && (
                    <li>
                      <button
                        onClick={() => {
                          navigate("/gitea");
                          onNavigate?.();
                        }}
                        className={`w-full flex items-center py-2 rounded transition ${
                          collapsed ? "justify-center" : "px-4 space-x-3"
                        } ${
                          isGiteaActive
                            ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                            : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                        }`}
                      >
                        <FaCodeBranch />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          Gitea
                        </span>
                      </button>
                    </li>
                  )}

                  {canSeeMailchimp && (
                    <li>
                      <button
                        onClick={() => {
                          navigate("/integrations/mailchimp");
                          onNavigate?.();
                        }}
                        className={`w-full flex items-center py-2 rounded transition ${
                          collapsed ? "justify-center" : "px-4 space-x-3"
                        } ${
                          isMailchimpActive
                            ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                            : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                        }`}
                      >
                        <FaEnvelope />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          Mailchimp
                        </span>
                      </button>
                    </li>
                  )}

                  {canViewFinanceSettings && (
                    <li>
                      <button
                        onClick={() => {
                          navigate("/integrations/eboekhouden");
                          onNavigate?.();
                        }}
                        className={`w-full flex items-center py-2 rounded transition ${
                          collapsed ? "justify-center" : "px-4 space-x-3"
                        } ${
                          isEBoekhoudenActive
                            ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                            : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                        }`}
                      >
                        <FaBook />
                        <span className={`${collapsed ? "sr-only" : "inline"}`}>
                          e-Boekhouden
                        </span>
                      </button>
                    </li>
                  )}

                  {canSeeNoctis && (
                    <li>
                      <div
                        className={`w-full flex items-center rounded transition py-2 ${
                          collapsed ? "justify-center" : "justify-between px-4"
                        } ${
                          isNoctisActive
                            ? "bg-indigo-600 shadow-inner dark:bg-slate-800/90"
                            : "hover:bg-indigo-500/80 dark:hover:bg-slate-800/70"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedSection("noctis");
                            navigate("/noctis");
                            onNavigate?.();
                          }}
                          className={`flex items-center ${
                            collapsed ? "" : "flex-1"
                          } focus:outline-none`}
                        >
                          <span
                            className={`flex items-center ${
                              collapsed ? "" : "space-x-3"
                            }`}
                          >
                            <FaMoon />
                            <span
                              className={`${collapsed ? "sr-only" : "inline"}`}
                            >
                              {t("noctis.menu", { defaultValue: "Noctis" })}
                            </span>
                          </span>
                        </button>

                        {!collapsed && (
                          <button
                            type="button"
                            onClick={() => toggleSection("noctis")}
                            className="ml-2 rounded p-1 hover:bg-indigo-500/60 focus:outline-none"
                            aria-label={
                              expandedSection === "noctis"
                                ? t("navigation.collapse_section", {
                                    defaultValue: "Collapse",
                                  })
                                : t("navigation.expand_section", {
                                    defaultValue: "Expand",
                                  })
                            }
                          >
                            {expandedSection === "noctis" ? (
                              <FaChevronUp />
                            ) : (
                              <FaChevronDown />
                            )}
                          </button>
                        )}
                      </div>

                      {!collapsed && expandedSection === "noctis" && (
                        <ul className="ml-4 mt-1 space-y-1">
                          <NavItem
                            label={t("noctis.object_types.title", {
                              defaultValue: "Object types",
                            })}
                            path="/noctis/object-types"
                          />
                          <NavItem
                            label={t("noctis.console.title", {
                              defaultValue: "Noctis console",
                            })}
                            path="/noctis/console"
                          />
                          <NavItem
                            label={t("noctis.events.title", {
                              defaultValue: "Noctis logs",
                            })}
                            path="/noctis/events"
                          />
                          <NavItem
                            label={t("noctis.outbox.title", {
                              defaultValue: "Noctis queue",
                            })}
                            path="/noctis/queue"
                          />
                          <NavItem
                            label={t("noctis.status.title", {
                              defaultValue: "Reference status",
                            })}
                            path="/noctis/reference-status"
                          />
                        </ul>
                      )}
                    </li>
                  )}
                </>
              )}
            </ul>
          </nav>
        )}
      </div>

      {!collapsed && (
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-b from-indigo-950/75 via-indigo-950/55 to-slate-950/75 backdrop-blur-md" />
          <div className="relative rounded-3xl p-3">
            <UserMenu
              onProfilePath="/profile"
              variant="admin"
              backendOnline={backendOnline}
              buttonClassName="w-full items-center gap-2 text-white"
              textClassName="text-white"
              menuItemsClassName="bg-white/95 text-gray-900 border border-white/40 shadow-xl rounded-xl overflow-hidden dark:bg-gray-900/95 dark:text-gray-100 dark:border-gray-700"
              itemsWrapperClassName="mt-3 w-full"
              onMenuButtonClick={collapseSections}
              onReportIssue={onReportIssue}
            />

            <div className="mt-2 text-center text-xs text-white/70">
              <button
                type="button"
                className="w-full rounded px-2 py-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
                onClick={() => setShowBackendVersion((v) => !v)}
                aria-expanded={showBackendVersion}
              >
                {t("common.version", { defaultValue: "Version" })} {APP_VERSION}
              </button>

              {showBackendVersion && (
                <div className="mt-1">
                  {backendVersionLoading ? (
                    <>
                      {t("common.backend_version", { defaultValue: "Backend" })}{" "}
                      …
                    </>
                  ) : backendVersion ? (
                    backendVersion === APP_VERSION ? (
                      <>
                        {t("common.version_match", {
                          defaultValue: "Frontend and backend match",
                        })}
                        : {APP_VERSION}
                      </>
                    ) : (
                      <>
                        {t("common.version_mismatch", {
                          defaultValue: "Version mismatch",
                        })}
                        :{" "}
                        {t("common.frontend_version", {
                          defaultValue: "Frontend",
                        })}{" "}
                        {APP_VERSION} •{" "}
                        {t("common.backend_version", {
                          defaultValue: "Backend",
                        })}{" "}
                        {backendVersion}
                      </>
                    )
                  ) : (
                    <>
                      {t("common.backend_version", { defaultValue: "Backend" })}{" "}
                      {t("common.not_available", { defaultValue: "-" })}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
